import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { RoundFlag } from "./RoundFlag";

const hexToRgba = (hex, a) => {
  const h = (hex || "#050a19").replace("#", "");
  const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(n.slice(0, 2), 16) || 0;
  const g = parseInt(n.slice(2, 4), 16) || 0;
  const b = parseInt(n.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

// Entry "splash": a darkened overlay with an uploaded image that bursts in
// (retro-explosion), then disappears. Can require a language selection (2 buttons
// PT/EN) that sets the site language and only then dismisses. Fully configurable in the BO.
export default function IntroSplash() {
  const { settings, lang, setLang, confirmLang, langConfirmed } = useApp();
  const { pathname } = useLocation();
  // Pick the intro image by the visitor's BROWSER language (not the site language,
  // which is still "pt" by default before they choose). If the preferred browser
  // language starts with "pt" (pt, pt-PT, pt-BR) show the PT image; otherwise show
  // the EN image. Falls back to the PT image when the EN one is not configured.
  const browserPrefersPt = (() => {
    const list = (navigator.languages && navigator.languages.length)
      ? navigator.languages
      : [navigator.language || navigator.userLanguage || ""];
    return list.some((l) => (l || "").toLowerCase().startsWith("pt"));
  })();
  const enabled = !!settings?.intro_enabled;
  const img = browserPrefersPt
    ? (settings?.intro_image_path_pt || settings?.intro_image_path_en)
    : (settings?.intro_image_path_en || settings?.intro_image_path_pt);
  const src = img ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${img}` : "";
  const durationMs = Math.max(0.5, Number(settings?.intro_duration ?? 3)) * 1000;
  const enter = settings?.intro_enter_effect || "zoom-in";
  const exit = settings?.intro_exit_effect || "explosion";
  const overlay = Math.min(100, Math.max(0, Number(settings?.intro_overlay ?? 80))) / 100;
  const overlayColor = settings?.intro_overlay_color || "#050a19";
  const animSpeed = Math.max(0.3, Number(settings?.intro_anim_speed ?? 0.9));
  const frequency = settings?.intro_frequency || "session";
  const languageSelect = !!settings?.intro_language_select;
  const requireClick = !!settings?.intro_require_click || languageSelect;
  const showBtn = settings?.intro_button_enabled !== false && !languageSelect;
  const isRetro = enter === "retro-explosion";
  const btnText = lang === "en"
    ? (settings?.intro_button_text_en || "Enter")
    : (settings?.intro_button_text_pt || "Entrar");
  const captionPt = settings?.intro_caption_pt ?? "Escolhe o teu idioma";
  const captionEn = settings?.intro_caption_en ?? "Choose your language";
  const caption = lang === "en" ? captionEn : captionPt;
  // Editable labels for the language-selection screens (fallback to defaults).
  const lblPt = settings?.intro_btn_pt_label || "Português";
  const lblEn = settings?.intro_btn_en_label || "English";
  const variantCaption = lang === "en"
    ? (settings?.intro_variant_caption_en || "Portuguese — choose the variant")
    : (settings?.intro_variant_caption_pt || "Português — escolhe a variante");
  const lblEu = settings?.intro_variant_eu_label || "Português (Europa)";
  const lblBr = settings?.intro_variant_br_label || "Português (Brasil)";
  const backLbl = lang === "en"
    ? (settings?.intro_back_label_en || "Back")
    : (settings?.intro_back_label_pt || "Voltar");

  // Framing effects (configurable in Admin → Efeitos).
  const featherOn = settings?.intro_feather_enabled !== false;
  const featherAmt = Math.min(45, Math.max(0, Number(settings?.intro_feather_amount ?? 10)));
  const tiltOn = settings?.intro_tilt_enabled !== false;
  const tiltX = Math.max(-30, Math.min(30, Number(settings?.intro_tilt_x ?? 4)));
  const tiltY = Math.max(-30, Math.min(30, Number(settings?.intro_tilt_y ?? -7)));
  // Image sizing + visual effects.
  const sizeW = Math.min(100, Math.max(20, Number(settings?.intro_image_size ?? 86)));
  const sizeH = Math.min(100, Math.max(20, Number(settings?.intro_image_height ?? 82)));
  const fit = settings?.intro_image_fit || "contain";
  const imgOpacity = Math.min(1, Math.max(0.1, Number(settings?.intro_image_opacity ?? 1)));
  const preset = settings?.intro_image_preset || "none";
  const blur = Math.min(20, Math.max(0, Number(settings?.intro_image_blur ?? 0)));
  const fullBg = settings?.intro_image_fullbg === true;
  // In full-screen background mode, size/fit are forced for a true edge-to-edge fit.
  const effSizeW = fullBg ? 100 : sizeW;
  const effSizeH = fullBg ? 100 : sizeH;
  const effFit = fullBg ? "cover" : fit;
  const presetFilter = ({
    none: "",
    vibrante: "saturate(150%) contrast(110%)",
    suave: "saturate(85%) brightness(105%)",
    bw: "grayscale(100%) contrast(105%)",
    sepia: "sepia(80%) saturate(120%)",
    noite: "hue-rotate(180deg) saturate(140%) brightness(90%)",
    sonho: "saturate(120%) brightness(108%) blur(1px)",
    quente: "sepia(30%) saturate(140%) hue-rotate(-10deg) brightness(105%)",
    frio: "hue-rotate(190deg) saturate(130%) brightness(95%)",
    neon: "saturate(200%) contrast(130%) brightness(110%)",
    retro: "sepia(60%) contrast(115%) saturate(130%) hue-rotate(-20deg)",
    cinema: "contrast(125%) saturate(120%) brightness(92%)",
    aurora: "hue-rotate(80deg) saturate(170%) brightness(105%)",
    oculto: "brightness(60%) contrast(130%) saturate(130%)",
    inverso: "invert(100%)",
    pastel: "saturate(70%) brightness(110%) contrast(90%)",
  })[preset] || "";
  const featherMask = `linear-gradient(to right, transparent 0, #000 ${featherAmt}%, #000 ${100 - featherAmt}%, transparent 100%), linear-gradient(to bottom, transparent 0, #000 ${featherAmt}%, #000 ${100 - featherAmt}%, transparent 100%)`;
  const imgStyle = {
    animationDuration: `${animSpeed}s`,
    "--intro-img-max-w": `${effSizeW}vw`,
    "--intro-img-max-h": `${effSizeH}vh`,
    "--intro-img-fit": effFit,
    filter: `${presetFilter} ${blur > 0 ? `blur(${blur}px)` : ""} drop-shadow(0 0 34px rgba(255,255,255,0.14)) drop-shadow(0 22px 60px rgba(0,0,0,0.5))`.trim(),
    ...(featherOn
      ? { WebkitMaskImage: featherMask, maskImage: featherMask, WebkitMaskComposite: "source-in", maskComposite: "intersect" }
      : { WebkitMaskImage: "none", maskImage: "none" }),
  };
  const frameStyle = { transform: tiltOn ? `perspective(1400px) rotateX(${tiltX}deg) rotateY(${tiltY}deg)` : "none", opacity: imgOpacity };

  const [show, setShow] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [introStep, setIntroStep] = useState("main");
  const firstRef = React.useRef(true);
  const langRef = React.useRef(lang);

  // Decide when to show: on first load (respecting frequency) and, unless in
  // language-selection mode, whenever the language is switched.
  useEffect(() => {
    if (!enabled) { confirmLang(); return; }
    // With no image there is nothing to show UNLESS the language-selection buttons
    // are enabled — in that case the selection screen must still appear.
    if (!src && !languageSelect) { confirmLang(); return; }
    // The entry/language splash belongs to the site entry (homepage) only. On any
    // deep link (/enroll, /train, /results, /rules…) go straight to the content —
    // no full-screen overlay flashing before the page. Language stays as stored.
    if (pathname !== "/") { confirmLang(); return; }
    const langChanged = langRef.current !== lang;
    langRef.current = lang;

    if (firstRef.current) {
      firstRef.current = false;
      // Arrived via a direct language link (/pt /br /en): language is already
      // confirmed, so skip the language-selection splash entirely.
      if (languageSelect && langConfirmed) return;
      if (frequency === "firstvisit" && localStorage.getItem("introShownEver") === "1") { confirmLang(); return; }
      if (frequency === "session" && sessionStorage.getItem("introShown") === "1") { confirmLang(); return; }
      if (frequency === "firstvisit") localStorage.setItem("introShownEver", "1");
      if (frequency === "session") sessionStorage.setItem("introShown", "1");
      // Only the language-selection splash gates the music; other modes let it play.
      if (!languageSelect) confirmLang();
      setExiting(false);
      setShow(true);
      return;
    }
    if (langChanged && !languageSelect) {
      setExiting(false);
      setShow(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, src, lang, pathname]);

  // Auto-dismiss after the configured duration (unless it must wait for a click).
  useEffect(() => {
    if (!show || exiting || requireClick) return;
    const t = setTimeout(() => setExiting(true), durationMs);
    return () => clearTimeout(t);
  }, [show, exiting, requireClick, durationMs]);

  // Once the exit starts, unmount after the animation finishes.
  useEffect(() => {
    if (!exiting) return;
    const t = setTimeout(() => setShow(false), 950);
    return () => clearTimeout(t);
  }, [exiting]);

  if (!show) return null;

  const dismiss = () => setExiting(true);
  const selectLang = (l) => {
    confirmLang(l);
    setExiting(true);
  };

  return (
    <div
      className={`intro-splash ${fullBg ? "intro-fullbg" : ""} ${exiting ? "intro-overlay-out" : ""}`}
      style={{ background: hexToRgba(overlayColor, overlay) }}
      onClick={languageSelect ? undefined : dismiss}
      data-testid="intro-splash"
    >
      {isRetro && !exiting && (
        <div className="intro-rays" aria-hidden data-testid="intro-rays">
          <span className="intro-sunburst" style={{ animationDuration: `${animSpeed}s` }} />
        </div>
      )}
      {isRetro && !exiting && <span className="intro-flash intro-flash-in" aria-hidden />}

      {src && (
        <div className="intro-img-frame" style={frameStyle}>
          <img
            src={src}
            alt=""
            style={imgStyle}
            className={`intro-img intro-enter-${enter} ${exiting ? `intro-exit-${exit}` : ""}`}
            data-testid="intro-splash-image"
          />
        </div>
      )}

      {languageSelect && !exiting && (
        <div className="intro-lang-wrap">
          {caption && introStep === "main" && <p className="intro-caption" data-testid="intro-caption">{caption}</p>}
          {introStep === "main" ? (
            <div className="intro-lang-row">
              <button type="button" className="intro-lang-btn intro-lang-pt"
                onClick={(e) => { e.stopPropagation(); setIntroStep("pt"); }} data-testid="intro-lang-pt">
                {lblPt}
              </button>
              <button type="button" className="intro-lang-btn intro-lang-en"
                onClick={(e) => { e.stopPropagation(); selectLang("en"); }} data-testid="intro-lang-en">
                {lblEn}
              </button>
            </div>
          ) : (
            <>
              <p className="intro-caption">{variantCaption}</p>
              <div className="intro-lang-row">
                <button type="button" className="intro-lang-btn intro-lang-flag intro-lang-pt"
                  onClick={(e) => { e.stopPropagation(); selectLang("pt"); }} data-testid="intro-lang-pt-eu">
                  <RoundFlag code="eu" size={24} /> {lblEu}
                </button>
                <button type="button" className="intro-lang-btn intro-lang-flag intro-lang-br"
                  onClick={(e) => { e.stopPropagation(); selectLang("br"); }} data-testid="intro-lang-pt-br">
                  <RoundFlag code="br" size={24} /> {lblBr}
                </button>
              </div>
              <button type="button" className="intro-back-btn"
                onClick={(e) => { e.stopPropagation(); setIntroStep("main"); }} data-testid="intro-lang-back">
                ← {backLbl}
              </button>
            </>
          )}
        </div>
      )}

      {showBtn && !exiting && (
        <button
          type="button"
          className="intro-enter-btn"
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          data-testid="intro-enter-btn"
        >
          {btnText}
        </button>
      )}

      {exiting && exit === "explosion" && (
        <>
          <span className="intro-flash" />
          {Array.from({ length: 16 }).map((_, i) => (
            <span
              key={i}
              className="intro-shard"
              style={{
                "--a": `${(360 / 16) * i}deg`,
                "--d": `${150 + (i % 3) * 60}px`,
                background: i % 3 === 0 ? "#FFD93D" : i % 3 === 1 ? "#FF6B6B" : "#ffffff",
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}
